<script lang="ts">
export default {
  name: 'LineStatusEditModal',
};
</script>

<script setup lang="ts">
import { ref, computed } from 'vue';
import ConfirmModal from './ConfirmModal.vue';
import TextField from '@/components/v2/TextField.vue';
import Toggle from '@/components/v2/Toggle.vue';
import AppButton from '@/components/v2/AppButton.vue';
import { LineStatus } from '@/types/LineStatus';
import { Trash2 } from '@lucide/vue';
import { DateTime } from 'luxon';

const props = withDefaults(
  defineProps<{
    lineStatus: LineStatus;
    onSave: (id: number, code: string, nonRecurring: boolean) => Promise<void>;
    onDelete: (id: number) => Promise<void>;
    /** When true, modal is controlled by parent (open on mount, no trigger button). */
    show?: boolean;
  }>(),
  { show: undefined },
);

const emit = defineEmits<{ closed: [] }>();

const code = ref('');
const nonRecurring = ref(false);
const confirmingDelete = ref(false);

const snapshotDate = computed(() => {
  if (!props.lineStatus.SnapshotDate) {
    return '--';
  }
  return DateTime.fromISO(props.lineStatus.SnapshotDate).toFormat('M/d/yyyy');
});

const isSaveDisabled = () => code.value.trim().length === 0;

async function save(): Promise<boolean> {
  document.body.focus();
  await props.onSave(props.lineStatus.ID, code.value, nonRecurring.value);
  return true;
}

async function doDelete(): Promise<boolean> {
  await props.onDelete(props.lineStatus.ID);
  confirmingDelete.value = false;
  emit('closed');
  return true;
}

function dialogOpened() {
  code.value = props.lineStatus.Code ?? '';
  nonRecurring.value = props.lineStatus.NonRecurring;
}
</script>

<template>
  <ConfirmModal
    title="Editing Line Status"
    :subtitle="lineStatus.Code ?? lineStatus.SourceStatusKey"
    :action="save"
    :is-confirm-disabled="isSaveDisabled"
    confirmText="Save"
    size="md"
    :show="show"
    @open="dialogOpened"
    @closed="emit('closed')">
    <div class="flex flex-col gap-4 p-2">
      <div class="flex gap-8 text-sm text-gray-600">
        <div>
          <span class="font-medium">Source Status Key:</span>
          {{ lineStatus.SourceStatusKey }}
        </div>
        <div>
          <span class="font-medium">Snapshot Date:</span>
          {{ snapshotDate }}
        </div>
      </div>
      <div>
        <label
          for="line-status-name"
          class="mb-1 block text-sm font-medium text-gray-700">
          Name
        </label>
        <TextField
          id="line-status-name"
          v-model="code"
          placeholder="Name"
          :data-test="`line-status-name-${lineStatus.ID}`" />
      </div>
      <Toggle
        id="line-status-non-recurring"
        v-model="nonRecurring"
        label="Non-Recurring"
        :data-test="`line-status-non-recurring-${lineStatus.ID}`" />
      <div class="border-t pt-4">
        <AppButton
          class="btn-outline btn-error btn-sm"
          :data-test="`delete-line-status-${lineStatus.ID}`"
          @click="confirmingDelete = true">
          <Trash2 :size="16" />
          Delete
        </AppButton>
      </div>
    </div>
  </ConfirmModal>

  <ConfirmModal
    v-if="confirmingDelete"
    title="Delete Line Status"
    :subtitle="lineStatus.Code ?? lineStatus.SourceStatusKey"
    :action="doDelete"
    confirmText="Delete"
    size="sm"
    :show="true"
    @closed="confirmingDelete = false">
    <p class="p-2">This line status will no longer appear in the platform. Are you sure?</p>
  </ConfirmModal>
</template>
